import { useAuth0 } from '@auth0/auth0-react'
import { Button, Card, Modal, ToggleSwitch, useToast } from '@helix-ds/react'
import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useFamilyPortalApiClient } from '../features/auth/hooks/useFamilyPortalApiClient'
import {
  CONSENT_CONFIG_ID,
  CONSENT_CONFIG_VERSION_ID,
  consentConfigVersionIdsFromPayload,
  type ConsentConfigPayload,
} from '../features/consent/consentConfig'
import LanguageSelector from '../features/preferences/components/LanguageSelector'
import { usePreferencesContext } from '../features/preferences/usePreferencesContext'
import DemographicsTextField from '../features/profile/components/DemographicsTextField'
import {
  DEMOGRAPHICS_FIELD_ORDER,
  mapServerValidationErrors,
  validateDemographics,
  validateField,
  type DemographicsField,
  type DemographicsFieldErrors,
  type DemographicsOriginalValues,
} from '../features/profile/validation'
import { publishProfileName } from '../hooks/useProfileName'
import { formatBirthdate, isoDatePart } from '../utils/date'

type ContactChangeKind = 'both' | 'email' | 'phone' | null

function contactChangeKind(emailChanged: boolean, phoneChanged: boolean): ContactChangeKind {
  if (emailChanged && phoneChanged) return 'both'
  if (emailChanged) return 'email'
  if (phoneChanged) return 'phone'
  return null
}

type ContactCopy = { id: string; defaultMessage: string }

// Picks the success title/message pair for a contact-info save. Each case is a single, complete
// sentence per react-intl id (never built by concatenating translated fragments, which breaks
// grammar once translated) — so a same-request "clear one, update the other" combo gets its own
// dedicated pair rather than reusing the plain "both updated" copy. Clearing both at once is
// impossible (blocked by the backend guardrail), so no "both removed" case is needed.
function resolveContactSuccessCopy(
  changeKind: ContactChangeKind,
  emailRemoved: boolean,
  phoneRemoved: boolean
): { title: ContactCopy; message: ContactCopy } | null {
  const bothTitle: ContactCopy = {
    id: 'profile.editModal.bothUpdateSuccessTitle',
    defaultMessage: 'Personal information updated',
  }
  if (changeKind === 'email') {
    return emailRemoved
      ? {
          title: { id: 'profile.editModal.emailRemovedSuccessTitle', defaultMessage: 'Email address removed' },
          message: {
            id: 'profile.editModal.emailRemovedSuccessMessage',
            defaultMessage: 'Your email address has been removed.',
          },
        }
      : {
          title: { id: 'profile.editModal.emailUpdateSuccessTitle', defaultMessage: 'Email address updated' },
          message: {
            id: 'profile.editModal.emailUpdateSuccessMessage',
            defaultMessage: 'Your email address has been updated.',
          },
        }
  }
  if (changeKind === 'phone') {
    return phoneRemoved
      ? {
          title: { id: 'profile.editModal.phoneRemovedSuccessTitle', defaultMessage: 'Phone number removed' },
          message: {
            id: 'profile.editModal.phoneRemovedSuccessMessage',
            defaultMessage: 'Your phone number has been removed.',
          },
        }
      : {
          title: { id: 'profile.editModal.phoneUpdateSuccessTitle', defaultMessage: 'Phone number updated' },
          message: {
            id: 'profile.editModal.phoneUpdateSuccessMessage',
            defaultMessage: 'Your phone number has been updated.',
          },
        }
  }
  if (changeKind === 'both') {
    if (emailRemoved) {
      return {
        title: bothTitle,
        message: {
          id: 'profile.editModal.emailRemovedPhoneUpdatedSuccessMessage',
          defaultMessage: 'Your email address has been removed and your phone number has been updated.',
        },
      }
    }
    if (phoneRemoved) {
      return {
        title: bothTitle,
        message: {
          id: 'profile.editModal.phoneRemovedEmailUpdatedSuccessMessage',
          defaultMessage: 'Your phone number has been removed and your email address has been updated.',
        },
      }
    }
    return {
      title: bothTitle,
      message: {
        id: 'profile.editModal.bothUpdateSuccessMessage',
        defaultMessage: 'Your email address and phone number have been updated.',
      },
    }
  }
  return null
}

type NotificationPreference = {
  emailItemId: string
  smsItemId: string
  titleId: string
  descriptionId: string
  titleDefault: string
  descriptionDefault: string
}

type ProfileDemographics = {
  firstName?: string | null
  lastName?: string | null
  dateOfBirth?: string | null
  emailAddress?: string | null
  phoneNumber?: string | null
}

export const NOTIFICATION_PREFERENCES: NotificationPreference[] = [
  {
    emailItemId: 'ACCOUNT_PREF_newResults_email',
    smsItemId: 'ACCOUNT_PREF_newResults_sms',
    titleId: 'profile.notifications.newResults.title',
    descriptionId: 'profile.notifications.newResults.description',
    titleDefault: 'New Results Available',
    descriptionDefault:
      'Notify me when new test results or information from my care team is ready to review, including cases where new information becomes available that may update or clarify previously provided results.',
  },
  {
    emailItemId: 'ACCOUNT_PREF_communitySupport_email',
    smsItemId: 'ACCOUNT_PREF_communitySupport_sms',
    titleId: 'profile.notifications.communitySupport.title',
    descriptionId: 'profile.notifications.communitySupport.description',
    titleDefault: 'Community & Support Opportunities',
    descriptionDefault:
      'Contact me with information about support groups, patient communities, or other opportunities to connect with families who share similar experiences.',
  },
  {
    emailItemId: 'ACCOUNT_PREF_researchOpportunities_email',
    smsItemId: 'ACCOUNT_PREF_researchOpportunities_sms',
    titleId: 'profile.notifications.researchOpportunities.title',
    descriptionId: 'profile.notifications.researchOpportunities.description',
    titleDefault: 'Research & Opportunities',
    descriptionDefault: 'Contact me about opportunities that may be related to my condition or my test results.',
  },
  {
    emailItemId: 'ACCOUNT_PREF_productFeedback_email',
    smsItemId: 'ACCOUNT_PREF_productFeedback_sms',
    titleId: 'profile.notifications.productFeedback.title',
    descriptionId: 'profile.notifications.productFeedback.description',
    titleDefault: 'Product Feedback Opportunities',
    descriptionDefault:
      'Contact me with user research or feedback opportunities to help improve my experience with Inflection Medicine.',
  },
]

const Profile: React.FC = () => {
  const intl = useIntl()
  const { user } = useAuth0()
  const apiClient = useFamilyPortalApiClient()
  const { state: languagePreferences } = usePreferencesContext()
  const [prefToggles, setPrefToggles] = useState<Record<string, boolean>>({})
  const [, setInitialPrefToggles] = useState<Record<string, boolean>>({})
  const [prefsLoading, setPrefsLoading] = useState(true)
  const [prefsLoadFailed, setPrefsLoadFailed] = useState(false)
  const [, setPrefSaving] = useState(false)
  const [consentConfigIds, setConsentConfigIds] = useState({
    configId: CONSENT_CONFIG_ID,
    configVersionId: CONSENT_CONFIG_VERSION_ID,
  })
  const { addNotification } = useToast()
  const [demographics, setDemographics] = useState<ProfileDemographics | null>(null)
  const [demographicsLoading, setDemographicsLoading] = useState(true)
  const [showEditDemographicsModal, setShowEditDemographicsModal] = useState(false)
  const [editForm, setEditForm] = useState<ProfileDemographics>({})
  const [demographicsSaving, setDemographicsSaving] = useState(false)
  const [contactUpdateKind, setContactUpdateKind] = useState<ContactChangeKind>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<DemographicsField, string>>>({})
  const fieldRefs = useRef<Partial<Record<DemographicsField, HTMLInputElement | null>>>({})
  // Snapshot of firstName/lastName as of modal-open, so the required check can tell "blanked
  // out an existing name" (invalid) apart from "never had one, untouched" (allowed).
  const originalNamesRef = useRef<DemographicsOriginalValues>({})
  const [demographicsSaveError, setDemographicsSaveError] = useState<string | null>(null)
  // Reserved for future change-password UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter kept for future UI
  const [, setChangePasswordExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setDemographicsLoading(true)
    apiClient
      .get('/profile/demographics')
      .then((res) => {
        if (cancelled) return
        setDemographicsLoading(false)
        if (res.ok) return res.json() as Promise<ProfileDemographics>
        setDemographics(null)
        return undefined
      })
      .then((data: ProfileDemographics | undefined) => {
        if (!cancelled && data) setDemographics(data)
      })
      .catch(() => {
        if (!cancelled) {
          setDemographicsLoading(false)
          setDemographics(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [apiClient])

  useEffect(() => {
    let cancelled = false
    async function loadPreferences() {
      setPrefsLoading(true)
      let configIds = consentConfigVersionIdsFromPayload(undefined)
      try {
        const configRes = await apiClient.get('/config/consents/secondary')
        if (!cancelled && configRes.ok) {
          const payload = (await configRes.json()) as ConsentConfigPayload
          configIds = consentConfigVersionIdsFromPayload(payload)
          setConsentConfigIds(configIds)
        }

        const params = new URLSearchParams({
          configId: configIds.configId,
          configVersionId: configIds.configVersionId,
          consentType: 'account_preferences',
        })
        const res = await apiClient.get(`/consents/secondary?${params.toString()}`)
        if (cancelled) return
        if (!res.ok) {
          setPrefsLoadFailed(true)
          return
        }
        const data = (await res.json()) as { itemStates?: Record<string, boolean> }
        const next: Record<string, boolean> = {}
        NOTIFICATION_PREFERENCES.forEach((item) => {
          next[item.emailItemId] = data?.itemStates?.[item.emailItemId] ?? false
          next[item.smsItemId] = data?.itemStates?.[item.smsItemId] ?? false
        })
        setPrefToggles(next)
        setInitialPrefToggles(next)
      } catch {
        if (!cancelled) setPrefsLoadFailed(true)
      } finally {
        if (!cancelled) setPrefsLoading(false)
      }
    }

    loadPreferences()
    return () => {
      cancelled = true
    }
  }, [apiClient])

  // Re-checks one field on blur using the same rules the save path applies, so inline feedback
  // and submit-time validation can never drift apart.
  const validateFieldOnBlur = (field: DemographicsField, value: string) => {
    const originalValue = field === 'firstName' || field === 'lastName' ? originalNamesRef.current[field] : undefined
    const error = validateField(field, value, { originalValue })
    setFieldErrors((prev) => ({ ...prev, [field]: error ? intl.formatMessage(error) : undefined }))
  }

  const clearFieldError = (field: DemographicsField) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }

  // Shows the given per-field errors and sends the user to the first one — in a scrolling modal
  // an error can otherwise go unseen. Returns false when there was nothing to show, so callers
  // can fall through to their own handling.
  const showFieldErrors = (errors: DemographicsFieldErrors): boolean => {
    const firstInvalidField = DEMOGRAPHICS_FIELD_ORDER.find((field) => errors[field])
    if (!firstInvalidField) return false
    const formatted: Partial<Record<DemographicsField, string>> = {}
    DEMOGRAPHICS_FIELD_ORDER.forEach((field) => {
      const copy = errors[field]
      if (copy) formatted[field] = intl.formatMessage(copy)
    })
    setFieldErrors(formatted)
    fieldRefs.current[firstInvalidField]?.focus()
    return true
  }

  const openEditDemographicsModal = () => {
    const authBirthdate =
      (user as { birthdate?: string })?.birthdate ??
      (user as { user_metadata?: { birthdate?: string } })?.user_metadata?.birthdate
    // Take the calendar-date part straight from the string so the edit input is
    // seeded with the same day the user was born, regardless of timezone.
    const dobStr = demographics?.dateOfBirth ?? isoDatePart(authBirthdate) ?? ''
    const firstName = demographics?.firstName ?? user?.given_name ?? user?.name?.split(' ')[0] ?? ''
    const lastName = demographics?.lastName ?? user?.family_name ?? user?.name?.split(' ').slice(1).join(' ') ?? ''
    originalNamesRef.current = { firstName, lastName }
    setEditForm({
      firstName,
      lastName,
      dateOfBirth: dobStr || undefined,
      emailAddress: resolvedEmail ?? '',
      phoneNumber: resolvedPhone ?? '',
    })
    setContactUpdateKind(null)
    setFieldErrors({})
    setDemographicsSaveError(null)
    setShowEditDemographicsModal(true)
  }

  const authPhone =
    (user as { phone_number?: string })?.phone_number ??
    (user as { user_metadata?: { phone_number?: string } })?.user_metadata?.phone_number
  // Once demographics has loaded, even a null field (e.g. after clearing an identifier) is
  // authoritative — only fall back to the cached Auth0 profile while demographics itself hasn't
  // successfully loaded yet (fetch failed, or hasn't completed). Otherwise a cleared email/phone
  // would incorrectly resurface from the (possibly stale) Auth0-cached value on reload.
  const resolvedEmail = demographics ? (demographics.emailAddress ?? null) : (user?.email ?? null)
  const resolvedPhone = demographics ? (demographics.phoneNumber ?? null) : (authPhone ?? null)
  const hasEmail = !!resolvedEmail
  const hasPhone = !!resolvedPhone

  const handlePrefToggle = (id: string) => {
    const next = { ...prefToggles, [id]: !prefToggles[id] }
    setPrefToggles(next)
    handlePrefSave(next)
  }

  const handlePrefSave = async (stateToSave?: Record<string, boolean>) => {
    const toSave = stateToSave ?? prefToggles
    setPrefSaving(true)
    // Only send channels the user can actually use; backend rejects entries for a
    // channel with no email/phone on file.
    const entries = NOTIFICATION_PREFERENCES.flatMap((item) => {
      const channels: { itemId: string; sectionTitle: string; isConsented: boolean }[] = []
      if (hasEmail) {
        channels.push({
          itemId: item.emailItemId,
          sectionTitle: 'Notifications',
          isConsented: !!toSave[item.emailItemId],
        })
      }
      if (hasPhone) {
        channels.push({ itemId: item.smsItemId, sectionTitle: 'Notifications', isConsented: !!toSave[item.smsItemId] })
      }
      return channels
    })
    try {
      const res = await apiClient.post('/consents/secondary', {
        configId: consentConfigIds.configId,
        configVersionId: consentConfigIds.configVersionId,
        consentType: 'account_preferences',
        entries,
      })
      if (res.ok) {
        setInitialPrefToggles({ ...toSave })
        return
      }
      const data = await res.json().catch(() => ({}))
      const message =
        typeof data?.detail === 'string'
          ? data.detail
          : res.status === 404
            ? intl.formatMessage({
                id: 'profile.accountPreferences.saveUnavailable',
                defaultMessage: 'Save is available after you have completed consent context.',
              })
            : intl.formatMessage({
                id: 'profile.accountPreferences.saveError',
                defaultMessage: "We couldn't save your preferences. Please try again later.",
              })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'profile.accountPreferences.saveErrorTitle',
          defaultMessage: 'Could not save preferences',
        }),
        message,
      })
    } catch {
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'profile.accountPreferences.saveErrorTitle',
          defaultMessage: 'Could not save preferences',
        }),
        message: intl.formatMessage({
          id: 'profile.accountPreferences.saveErrorMessage',
          defaultMessage: 'We could not save your preferences. Please try again later.',
        }),
      })
    } finally {
      setPrefSaving(false)
    }
  }

  const handleDemographicsSave = async () => {
    const submittedEmail = (editForm.emailAddress ?? '').trim()
    const submittedPhone = (editForm.phoneNumber ?? '').trim()
    setDemographicsSaveError(null)

    // Report every invalid field at once rather than one per submit.
    if (showFieldErrors(validateDemographics(editForm, originalNamesRef.current))) return
    // Both identifiers are login paths; leaving both blank would lock the family user out.
    // Mirrors the backend's 409 guardrail (see app/profile/service.py) so this is caught before
    // ever reaching the network.
    if (!submittedEmail && !submittedPhone) {
      setDemographicsSaveError(
        intl.formatMessage({
          id: 'profile.editModal.clearGuardrailMessage',
          defaultMessage:
            "This is your only verified way to log in, so it can't be removed. Add and verify an email or phone number first, then try again.",
        })
      )
      return
    }

    const currentEmail = resolvedEmail ?? ''
    const currentPhone = resolvedPhone ?? ''
    const emailChanged = submittedEmail.toLowerCase() !== currentEmail.trim().toLowerCase()
    const phoneChanged = submittedPhone !== currentPhone.trim()
    const changeKind = contactChangeKind(emailChanged, phoneChanged)
    const failureMessageId =
      changeKind === 'both'
        ? 'profile.editModal.bothUpdateErrorMessage'
        : changeKind === 'email'
          ? 'profile.editModal.emailUpdateErrorMessage'
          : changeKind === 'phone'
            ? 'profile.editModal.phoneUpdateErrorMessage'
            : 'profile.editModal.saveErrorMessage'
    const failureMessageDefault =
      changeKind === 'both'
        ? "We weren't able to update your account just now. Your email address and phone number haven't changed. Please try again shortly."
        : changeKind === 'email'
          ? "We weren't able to update your account just now. Your email address hasn't changed. Please try again shortly."
          : changeKind === 'phone'
            ? "We weren't able to update your account just now. Your phone number hasn't changed. Please try again shortly."
            : 'Please try again later.'
    const failureMessage = intl.formatMessage({ id: failureMessageId, defaultMessage: failureMessageDefault })

    const restoreEffectiveProfile = async () => {
      let effectiveEmail = currentEmail
      let effectivePhone = currentPhone
      try {
        const response = await apiClient.get('/profile/demographics')
        if (response.ok) {
          const currentProfile = (await response.json()) as ProfileDemographics
          setDemographics(currentProfile)
          // A freshly successful fetch is authoritative, including an explicit null (e.g. the
          // identifier was actually cleared) — don't fall back to the pre-edit value here.
          effectiveEmail = currentProfile.emailAddress ?? ''
          effectivePhone = currentProfile.phoneNumber ?? ''
        }
      } catch {
        // The original value remains the safest UI fallback when reconciliation is unavailable.
      }
      setEditForm((prev) => ({
        ...prev,
        emailAddress: effectiveEmail || undefined,
        phoneNumber: effectivePhone || undefined,
      }))
    }

    setContactUpdateKind(changeKind)
    setFieldErrors({})
    setDemographicsSaving(true)
    try {
      const res = await apiClient.patch('/profile/demographics', {
        firstName: editForm.firstName || null,
        lastName: editForm.lastName || null,
        dateOfBirth: editForm.dateOfBirth || null,
        emailAddress: submittedEmail || null,
        phoneNumber: submittedPhone || null,
      })
      if (!res.ok) {
        // The server enforces rules this client deliberately doesn't mirror, so a 422 is a
        // correctable field problem — not the transient failure the generic copy describes.
        // Keep what the user typed (no restore) so they can fix it in place.
        if (res.status === 422) {
          const data = await res.json().catch(() => ({}) as { detail?: unknown })
          if (showFieldErrors(mapServerValidationErrors(data?.detail))) return
          setDemographicsSaveError(failureMessage)
          if (changeKind) await restoreEffectiveProfile()
          return
        }
        // A 409 conflict's detail is a support-contact message crafted for display; other
        // failures use our own fixed copy so we never surface backend/system-specific text.
        if (res.status === 409) {
          const data = await res.json().catch(() => ({}) as { detail?: unknown })
          setDemographicsSaveError(
            typeof data?.detail === 'string' && data.detail.trim()
              ? data.detail
              : intl.formatMessage({
                  id: 'profile.editModal.contactConflictFallbackMessage',
                  defaultMessage: "This information can't be used for your account. Please contact support.",
                })
          )
        } else {
          setDemographicsSaveError(failureMessage)
        }
        if (changeKind) {
          await restoreEffectiveProfile()
        }
        return
      }

      const data = (await res.json()) as ProfileDemographics
      setDemographics(data)
      publishProfileName({
        firstName: data.firstName ?? null,
        lastName: data.lastName ?? null,
        emailAddress: data.emailAddress ?? null,
      })
      setShowEditDemographicsModal(false)
      const successCopy = resolveContactSuccessCopy(changeKind, !submittedEmail, !submittedPhone)
      if (successCopy) {
        addNotification({
          type: 'success',
          title: intl.formatMessage(successCopy.title),
          message: intl.formatMessage(successCopy.message),
        })
      }
    } catch {
      setDemographicsSaveError(failureMessage)
      if (changeKind) {
        await restoreEffectiveProfile()
      }
    } finally {
      setContactUpdateKind(null)
      setDemographicsSaving(false)
    }
  }

  const authBirthdate =
    (user as { birthdate?: string })?.birthdate ??
    (user as { user_metadata?: { birthdate?: string } })?.user_metadata?.birthdate
  const firstName = demographics?.firstName ?? user?.given_name ?? user?.name?.split(' ')[0] ?? '—'
  const lastName = demographics?.lastName ?? user?.family_name ?? user?.name?.split(' ').slice(1).join(' ') ?? '—'
  const birthdate = demographics?.dateOfBirth ?? authBirthdate
  const emailDisplay = resolvedEmail ?? '—'
  const phoneDisplay = resolvedPhone ?? '—'

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-1 sm:px-0">
      <section className="space-y-2">
        <h1 className="text-text-heading text-4xl font-semibold tracking-tight">
          <FormattedMessage id="profile.heading" defaultMessage="My Profile" />
        </h1>
        <p className="text-text-body text-base">
          <FormattedMessage
            id="profile.body"
            defaultMessage="View and manage your personal information and account preferences."
          />
        </p>
      </section>

      {/* Personal Information */}
      <Card className="personal-information-card">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-text-heading text-2xl font-semibold tracking-tight">
            <FormattedMessage id="profile.personalInfo.heading" defaultMessage="Personal Information" />
          </h2>
          <Button
            variant="tertiary"
            size="sm"
            aria-label={intl.formatMessage({
              id: 'profile.personalInfo.editAriaLabel',
              defaultMessage: 'Edit personal information',
            })}
            onClick={openEditDemographicsModal}
          >
            <FormattedMessage id="profile.personalInfo.editButton" defaultMessage="Edit" />
          </Button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <p className="text-text-subtle text-sm">
              <FormattedMessage id="profile.personalInfo.firstName" defaultMessage="First Name" />
            </p>
            <p className="profile-value text-text-body mt-1 text-base">{demographicsLoading ? '…' : firstName}</p>
          </div>
          <div>
            <p className="text-text-subtle text-sm">
              <FormattedMessage id="profile.personalInfo.lastName" defaultMessage="Last Name" />
            </p>
            <p className="profile-value text-text-body mt-1 text-base">{demographicsLoading ? '…' : lastName}</p>
          </div>
          <div>
            <p className="text-text-subtle text-sm">
              <FormattedMessage id="profile.personalInfo.dateOfBirth" defaultMessage="Date of Birth" />
            </p>
            <p className="profile-value text-text-body mt-1 text-base">
              {demographicsLoading ? '…' : formatBirthdate(birthdate)}
            </p>
          </div>
          <div>
            <p className="text-text-subtle text-sm">
              <FormattedMessage id="profile.personalInfo.emailAddress" defaultMessage="Email Address" />
            </p>
            <p className="profile-value text-text-body mt-1 text-base">{demographicsLoading ? '…' : emailDisplay}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-text-subtle text-sm">
              <FormattedMessage id="profile.personalInfo.phoneNumber" defaultMessage="Phone Number" />
            </p>
            <p className="profile-value text-text-body mt-1 text-base">{demographicsLoading ? '…' : phoneDisplay}</p>
          </div>
        </div>
      </Card>

      {/* Edit Personal Information modal */}
      <Modal
        isOpen={showEditDemographicsModal}
        onClose={() => {
          if (!demographicsSaving) setShowEditDemographicsModal(false)
        }}
        title={intl.formatMessage({ id: 'profile.editModal.title', defaultMessage: 'Personal Information' })}
        size="lg"
      >
        <div
          className="border-border-brand-subtle bg-background-brand-softer dark:border-brand-800 mb-6 flex gap-3 rounded-xl border p-4"
          role="note"
        >
          <span
            className="text-brand-600 dark:text-brand-400 bg-brand-200 dark:bg-brand-800 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
            aria-hidden
          >
            i
          </span>
          <p className="text-text-body text-sm">
            <FormattedMessage
              id="profile.editModal.hint"
              defaultMessage="Update your basic information. This can be edited at any time."
            />
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <DemographicsTextField
            id="edit-firstName"
            type="text"
            required
            label={intl.formatMessage({ id: 'profile.personalInfo.firstName', defaultMessage: 'First Name' })}
            value={editForm.firstName ?? ''}
            error={fieldErrors.firstName}
            inputRef={(element) => (fieldRefs.current.firstName = element)}
            onChange={(value) => {
              setEditForm((prev) => ({ ...prev, firstName: value || undefined }))
              clearFieldError('firstName')
            }}
            onBlur={(value) => validateFieldOnBlur('firstName', value)}
          />
          <DemographicsTextField
            id="edit-lastName"
            type="text"
            required
            label={intl.formatMessage({ id: 'profile.personalInfo.lastName', defaultMessage: 'Last Name' })}
            value={editForm.lastName ?? ''}
            error={fieldErrors.lastName}
            inputRef={(element) => (fieldRefs.current.lastName = element)}
            onChange={(value) => {
              setEditForm((prev) => ({ ...prev, lastName: value || undefined }))
              clearFieldError('lastName')
            }}
            onBlur={(value) => validateFieldOnBlur('lastName', value)}
          />
          <DemographicsTextField
            id="edit-dateOfBirth"
            type="date"
            required
            label={intl.formatMessage({ id: 'profile.personalInfo.dateOfBirth', defaultMessage: 'Date of Birth' })}
            value={editForm.dateOfBirth ?? ''}
            onChange={(value) => setEditForm((prev) => ({ ...prev, dateOfBirth: value || undefined }))}
          />
          <DemographicsTextField
            id="edit-emailAddress"
            type="email"
            label={intl.formatMessage({ id: 'profile.personalInfo.emailAddress', defaultMessage: 'Email Address' })}
            value={editForm.emailAddress ?? ''}
            error={fieldErrors.emailAddress}
            inputRef={(element) => (fieldRefs.current.emailAddress = element)}
            onChange={(value) => {
              setEditForm((prev) => ({ ...prev, emailAddress: value || undefined }))
              clearFieldError('emailAddress')
            }}
            onBlur={(value) => validateFieldOnBlur('emailAddress', value)}
          />
          <DemographicsTextField
            id="edit-phoneNumber"
            type="tel"
            wrapperClassName="sm:col-span-2"
            label={intl.formatMessage({ id: 'profile.personalInfo.phoneNumber', defaultMessage: 'Phone Number' })}
            value={editForm.phoneNumber ?? ''}
            error={fieldErrors.phoneNumber}
            inputRef={(element) => (fieldRefs.current.phoneNumber = element)}
            onChange={(value) => {
              setEditForm((prev) => ({ ...prev, phoneNumber: value || undefined }))
              clearFieldError('phoneNumber')
            }}
            onBlur={(value) => validateFieldOnBlur('phoneNumber', value)}
          />
        </div>
        {demographicsSaving && (
          <p className="text-text-body mt-4 text-sm" role="status" aria-live="polite">
            {intl.formatMessage({
              id:
                contactUpdateKind === 'both'
                  ? 'profile.editModal.bothUpdating'
                  : contactUpdateKind === 'email'
                    ? 'profile.editModal.emailUpdating'
                    : contactUpdateKind === 'phone'
                      ? 'profile.editModal.phoneUpdating'
                      : 'profile.editModal.savingButton',
              defaultMessage:
                contactUpdateKind === 'both'
                  ? 'Updating your email address and phone number…'
                  : contactUpdateKind === 'email'
                    ? 'Updating your email address…'
                    : contactUpdateKind === 'phone'
                      ? 'Updating your phone number…'
                      : 'Saving…',
            })}
          </p>
        )}
        {demographicsSaveError && (
          <p className="text-text-fg-error mt-4 text-sm" role="alert">
            {demographicsSaveError}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" disabled={demographicsSaving} onClick={() => setShowEditDemographicsModal(false)}>
            <FormattedMessage id="profile.editModal.discardButton" defaultMessage="Discard Changes" />
          </Button>
          <Button
            variant="primary"
            disabled={demographicsSaving}
            aria-busy={demographicsSaving}
            onClick={handleDemographicsSave}
          >
            {demographicsSaving
              ? intl.formatMessage({ id: 'profile.editModal.savingButton', defaultMessage: 'Saving…' })
              : intl.formatMessage({ id: 'profile.editModal.doneButton', defaultMessage: 'Done' })}
          </Button>
        </div>
      </Modal>

      {/* Account Preferences */}
      <Card>
        <h2 className="text-text-heading mb-4 text-2xl font-semibold tracking-tight">
          <FormattedMessage id="profile.accountPreferences.heading" defaultMessage="Account Preferences" />
        </h2>
        {prefsLoading && (
          <p className="text-text-body text-sm" aria-live="polite">
            <FormattedMessage id="profile.accountPreferences.loading" defaultMessage="Loading account preferences…" />
          </p>
        )}
        {!prefsLoading && prefsLoadFailed && (
          <p className="text-text-fg-error text-sm" role="alert">
            <FormattedMessage
              id="profile.accountPreferences.loadError"
              defaultMessage="We couldn't load account preferences."
            />
          </p>
        )}
        {!prefsLoading && !prefsLoadFailed && (
          <>
            <h3 className="text-text-heading mb-4 text-lg font-semibold">
              <FormattedMessage
                id="profile.accountPreferences.fallbackSectionTitle"
                defaultMessage="Notification preferences"
              />
            </h3>
            <div className="space-y-4">
              {NOTIFICATION_PREFERENCES.map((item) => {
                const title = intl.formatMessage({
                  id: item.titleId,
                  defaultMessage: item.titleDefault,
                })
                const description = intl.formatMessage({
                  id: item.descriptionId,
                  defaultMessage: item.descriptionDefault,
                })
                const smsOn = hasPhone && !!prefToggles[item.smsItemId]
                const emailOn = hasEmail && !!prefToggles[item.emailItemId]
                return (
                  <div key={item.emailItemId} className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-text-heading text-base font-semibold">{title}</p>
                      <p className="text-text-body mt-1 text-sm">{description}</p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      {/* SMS toggle */}
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-text-subtle text-xs">
                            <FormattedMessage id="profile.notifications.channel.sms" defaultMessage="SMS" />
                          </span>
                          <ToggleSwitch
                            sizing="sm"
                            checked={smsOn}
                            aria-labelledby={undefined}
                            aria-label={
                              intl.formatMessage(
                                { id: 'profile.notifications.channel.sms', defaultMessage: 'SMS' },
                                {}
                              ) +
                              ' — ' +
                              title
                            }
                            disabled={!hasPhone}
                            onChange={() => handlePrefToggle(item.smsItemId)}
                          />
                        </div>
                        {!hasPhone && (
                          <p className="text-text-subtle text-right text-xs">
                            <FormattedMessage id="profile.notifications.smsDisabledNoPhone" defaultMessage="No Phone" />{' '}
                            <button
                              type="button"
                              onClick={openEditDemographicsModal}
                              aria-label={intl.formatMessage({
                                id: 'profile.notifications.addPhoneAriaLabel',
                                defaultMessage: 'Add phone number in Personal Information',
                              })}
                              className="text-brand-600 dark:text-brand-400 underline"
                            >
                              <FormattedMessage id="profile.notifications.addContactCta" defaultMessage="Add" />
                            </button>
                          </p>
                        )}
                      </div>
                      {/* Email toggle */}
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-text-subtle text-xs">
                            <FormattedMessage id="profile.notifications.channel.email" defaultMessage="Email" />
                          </span>
                          <ToggleSwitch
                            sizing="sm"
                            checked={emailOn}
                            aria-labelledby={undefined}
                            aria-label={
                              intl.formatMessage(
                                { id: 'profile.notifications.channel.email', defaultMessage: 'Email' },
                                {}
                              ) +
                              ' — ' +
                              title
                            }
                            disabled={!hasEmail}
                            onChange={() => handlePrefToggle(item.emailItemId)}
                          />
                        </div>
                        {!hasEmail && (
                          <p className="text-text-subtle text-right text-xs">
                            <FormattedMessage
                              id="profile.notifications.emailDisabledNoEmail"
                              defaultMessage="No Email"
                            />{' '}
                            <button
                              type="button"
                              onClick={openEditDemographicsModal}
                              aria-label={intl.formatMessage({
                                id: 'profile.notifications.addEmailAriaLabel',
                                defaultMessage: 'Add email address in Personal Information',
                              })}
                              className="text-brand-600 dark:text-brand-400 underline"
                            >
                              <FormattedMessage id="profile.notifications.addContactCta" defaultMessage="Add" />
                            </button>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {/* SMS compliance disclosure */}
            <p className="text-text-subtle mt-4 text-xs">
              <FormattedMessage
                id="profile.notifications.smsDisclosure"
                defaultMessage="*By enabling SMS notifications, you certify that you are at least 18 years old and agree to receive text messages from Inflection Medicine at the mobile number on file. You may choose to receive marketing and/or non-marketing text messages. Consent is not a condition of service. Message frequency varies. Message & data rates may apply. Reply HELP for help or STOP to cancel. View our {privacyPolicy} and {termsAndConditions}."
                values={{
                  privacyPolicy: (
                    <a
                      href="https://inflectionmedicine.com/documents/inflection-medicine-privacy-policy-jan2026.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      <FormattedMessage id="login.footer.privacyPolicy" defaultMessage="Privacy Policy" />
                    </a>
                  ),
                  termsAndConditions: (
                    <a
                      href="https://inflectionmedicine.com/documents/inflection-website-terms-and-conditions.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      <FormattedMessage id="login.footer.termsAndConditions" defaultMessage="Terms &amp; Conditions" />
                    </a>
                  ),
                }}
              />
            </p>
          </>
        )}
        {languagePreferences.status === 'ready' && (
          <>
            <hr className="border-border-base my-6" />
            <LanguageSelector />
            <hr className="border-border-base my-6" />
          </>
        )}
        <Link
          to="/consents/manage"
          className="text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-400 text-base leading-6 font-bold underline"
        >
          <FormattedMessage id="profile.reviewConsentsLink" defaultMessage="Review or Update Consents" />
        </Link>
      </Card>
    </div>
  )
}

export default Profile
